"use client";

import React, {useEffect, useRef, useState} from "react";

import DecorationLine from "@/components/global/ui/DecorationLine";
import CornerChip from "@/components/global/ui/CornerChip";
import DecorationSquares from "@/components/global/ui/DecorationSquares";
import Chip from "@/components/global/ui/Chip";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import {PageHomeNewSchema} from "@/sanity/pages/PageHomeNewTemplate";

import wikis from "@/public/home-new/wikis.svg";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

export default function TableSection({
  data,
  className
}: {
  data: PageHomeNewSchema;
  className?: string;
}) {
  const chipRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [chipPositions, setChipPositions] = useState<number[]>([]);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [activeColumnIndex, setActiveColumnIndex] = useState(0);

  const tableColors = [
    "#D9F9B9",
    "#E9B9F9",
    "#B9C0F9"
  ]

  const tableHeads = [
    data?.comparisonTableHead1,
    data?.comparisonTableHead2,
    data?.comparisonTableHead3,
  ]

  useEffect(() => {
    const calculatePositions = () => {
      if (!chipRefs.current.length) return;

      const allChipsMeasured = chipRefs.current.every(chip => chip && chip.offsetWidth > 0);
      if (!allChipsMeasured) return;

      const positions: number[] = [];
      let cumulativeLeft = 0;

      const remInPixels = parseFloat(getComputedStyle(document.documentElement).fontSize);

      chipRefs.current.forEach((chip) => {
        positions.push(cumulativeLeft);
        if (chip) {
          const widthInPixels = chip.offsetWidth;
          const widthInRem = widthInPixels / remInPixels;
          cumulativeLeft += widthInRem + 1;
        }
      });

      setChipPositions(positions);
    };

    const timeout1 = setTimeout(calculatePositions, 100);
    const timeout2 = setTimeout(calculatePositions, 300);
    const timeout3 = setTimeout(calculatePositions, 500);

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(calculatePositions);
    }

    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculatePositions, 100);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [data?.comparisonTable]);

  useEffect(() => {
    const handleResize = () => {
      const isLg = window.innerWidth >= 993;
      setIsLargeScreen(isLg);
    };

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const tableCell = (item: any, col: string, colIndex: number, rowIndex: number) => (
    <div
      className={cn(
        rowIndex === 0 ? "pt-[1rem] lg:pt-[6rem] pb-[2rem] lg:pb-[3rem]" : "py-[2rem] lg:py-[3rem]",
        colIndex === 0 ? "pl-[2rem] sm:pl-[3rem] lg:pl-0" : "lg:px-[2rem]",
        colIndex === 1 ? "pr-[2rem] sm:pr-[3rem] lg:pr-[2rem] lg:bg-[#D9F9B9]/20" : "pr-[2rem] sm:pr-[3rem] lg:pr-0"
      )}
    >
      <p className={cn(
        "mb-0 leading-[1.2] text-[2.25rem]",
        colIndex === 0 ? "font-geistmono uppercase" : "",
        colIndex === 1 ? "text-black/90 font-medium" : "text-black/60"
      )}>
        {item?.[col]}
      </p>
    </div>
  )

  return (
    <section className={cn(
      "relative overflow-clip pt-[13rem] lg:pt-[18rem]",
      className
    )}>
      <DecorationLine
        className="top-0 left-0"
        orientation="horizontal"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationSquares
        className="top-[2px]"
      />
      <div className="container flex flex-col items-center">
        <Chip
          image={data?.comparisonChip?.icon}
          text={data?.comparisonChip?.text}
        />

        {data?.comparisonTitle && (
          <div className="richtext w-[min(83.5rem,100%)]">
            <PortableText
              value={data.comparisonTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.comparisonCTA && (
          <CTACornerButton
            className="mt-[3rem]"
            data={data.comparisonCTA}
          />
        )}

        {data?.comparisonTable && (
          <div className="w-[min(187.75rem,100%)] relative mt-[9rem] lg:mt-[13rem]">
            <div className="absolute left-0 bottom-full lg:hidden pointer-events-none">
              {tableHeads?.map((head, index) => (
                <div
                  key={`chip-${index}`}
                  ref={el => {
                    (chipRefs.current[index] = el);
                  }}
                  className="absolute pointer-events-auto cursor-pointer"
                  style={{
                    left: chipPositions[index] !== undefined ? `${chipPositions[index]}rem` : `${index * 10}rem`,
                    bottom: 0
                  }}
                  onClick={() => setActiveColumnIndex(index)}
                >
                  <CornerChip
                    className="uppercase prose-p:text-[1.75rem] min-h-[4.5rem]"
                    text={head?.shortName ?? head?.name}
                    style={{
                      backgroundColor: tableColors[index]
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-[1rem] bg-white lg:bg-transparent">
              {tableHeads?.map((head, index) => (
                <div
                  key={index}
                  className={cn(
                    "col-span-2 lg:col-span-1 lg:bg-white relative flex flex-col items-start lg:items-center gap-[1.5rem] pl-[2rem] sm:pl-[3rem] pr-[1rem] lg:pr-[3rem] py-[2.5rem] sm:py-[3rem] lg:py-[4rem]",
                    index === 0 ? "lg:col-start-2" : "",
                    !isLargeScreen && index !== activeColumnIndex ? "hidden" : ""
                  )}
                  style={{
                    borderTop: `.375rem solid ${tableColors[index]}`
                  }}
                >
                  {index === 0 && (
                    <CornerChip
                      className="absolute -left-[1px] bottom-full hidden lg:inline-flex"
                      image={wikis}
                      text="BEST OPTION"
                    />
                  )}

                  <h3 className="!mb-0 text-[3rem] font-medium text-black/80 leading-none">{head?.name}</h3>
                  <p className="mb-0 text-[2.5rem] text-black/60 leading-none">{head?.text}</p>
                </div>
              ))}

              {data.comparisonTable.map((item, rowIndex) => (
                <React.Fragment key={rowIndex}>
                  {tableCell(item, 'col1', 0, rowIndex)}
                  {(isLargeScreen || activeColumnIndex === 0) && tableCell(item, 'col2', 1, rowIndex)}
                  {(isLargeScreen || activeColumnIndex === 1) && tableCell(item, 'col3', 2, rowIndex)}
                  {(isLargeScreen || activeColumnIndex === 2) && tableCell(item, 'col4', 3, rowIndex)}

                  {rowIndex < data.comparisonTable.length - 1 && (
                    <hr className="col-span-2 lg:col-span-4 border-t border-t-black/10 mx-[2rem] sm:mx-[3rem] lg:mx-0" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
